import { kea } from 'kea'
import PropTypes from 'prop-types'
import { find, get, map, forEach, omit } from 'lodash'
import {put, call, all, select} from 'redux-saga/effects'

import { Products, CartItems, Carts, VideoPlayers } from '@gratify-eu/entities'
import { selector as configSelector } from '~/config'

import entities from '~/entities'
import rootLogic from '~/scenes/logic'

const hasDescription = (product, videoPlayer) => {
  if(product.description) {
    return true;
  }

  let hasDescription = false;
  if(videoPlayer && videoPlayer.videos) {
    forEach(videoPlayer.videos, (video) => {
      if(video.description) {
        hasDescription = true;
        return false;
      }
    });
  }

  return hasDescription
};


export default kea({
  key: (props) => {
    const {id, gfyPlugin} = props;

    return `${gfyPlugin}-${id}`
  },

  connect: {
    actions: [
      rootLogic, [
        'setLoggedIn',
        'setLoggedOut'
      ]
    ]
  },

  path: (key) => {
    return ['plugins', 'video', key];
  },

  actions: ({ constants }) => ({
    addToCart: (product) => ({product}),
    loading: (productId) => ({productId}),
    loadSuccess: (product) => ({product}),
    loadFailed: true,
    trailerFinished: true,
    trailerRestarted: true,
    setActiveVideo: (videoIndex) => ({videoIndex})
  }),

  reducers: ({ actions, constants }) => ({
    loading: [false, PropTypes.bool, {
      [actions.loading]: () => true,
      [actions.loadSuccess]: () => false,
      [actions.loadFailed]: () => false,
    }],
    failed: [false, PropTypes.bool, {
      [actions.loadFailed]: () => true,
    }],
    trailerDone: [false, PropTypes.bool, {
      [actions.trailerFinished]: () => true,
      [actions.trailerRestarted]: () => false,
    }],
    activeIndex: [0, PropTypes.number, {
      [actions.setActiveVideo]: (_, payload) => {
        return payload.videoIndex || 0;
      }
    }]
  }),

  selectors : ({ constants, selectors }) => ({
    product: [
      () => [
        entities.selectors.products,
        (state, props) => props.productId
      ], (products, productId) => {
        if (productId) {
          const product = find(products, {id: productId});

          if (product) {
            return product;
          }
        }

        return null;
      },
      PropTypes.object
    ],

    videoPlayer: [
      () => [
        selectors.product,
        entities.selectors.videoPlayers,
      ], (product, videoPlayers) => {
        if(product) {
          return find(videoPlayers, {product: product.url});
        }

        return null;
      },
      PropTypes.object
    ],

    productVideo: [
      () => [
        selectors.videoPlayer,
        (state, props) => (props),
      ], (videoPlayer, props) => {
        const { videoId } = props || {};
        if(videoPlayer && videoId) {
          return find(videoPlayer.videos, {id: videoId});
        }

        return null;
      },
      PropTypes.object
    ],

    hasAccess: [
      () => [
        selectors.videoPlayer,
      ], (videoPlayer) => {
        return videoPlayer && videoPlayer.has_access;
      },
      PropTypes.bool
    ],

    activeVideo: [
      () => [
        selectors.hasAccess,
        selectors.videoPlayer,
        selectors.activeIndex,
        selectors.productVideo,
      ], (hasAccess, videoPlayer, activeIndex, productVideo) => {
        if(productVideo) {
          return productVideo;
        }
        if(videoPlayer) {
          return videoPlayer.videos[activeIndex] || videoPlayer.videos[0]
        }

        return null;
      },
      PropTypes.object
    ],

    hasQuantity: [
      () => [
        selectors.product
      ], (product) => {
        if(product) {
          const quantity = get(product, 'features.quantity')
          if(typeof quantity !== 'undefined') {
            return quantity > 0;
          }
        }

        return true
      },
      PropTypes.bool
    ],

    showPlaylist: [
      () => [
        selectors.videoPlayer,
        selectors.productVideo,
      ], (videoPlayer, productVideo) => {
        if(productVideo) {
          return false;
        }

        return videoPlayer && videoPlayer.videos && videoPlayer.videos.length > 1;
      },
      PropTypes.bool
    ],

    mainPoster: [
      () => [
        selectors.videoPlayer,
        selectors.productVideo,
      ], (videoPlayer, productVideo) => {
        if(productVideo && productVideo.thumbnail_image) {
          return productVideo.thumbnail_image;
        }

        if(videoPlayer && videoPlayer.main_poster_image) {
          return videoPlayer.main_poster_image;
        }

        return null;
      },
      PropTypes.string
    ],
    title: [
      () => [
        selectors.product,
        selectors.productVideo,
      ], (product, productVideo) => {
        if(productVideo && productVideo.name) {
          return productVideo.name;
        }

        if(product && product.name) {
          return product.name;
        }

        return null;
      },
      PropTypes.string
    ],
    description: [
      () => [
        selectors.product,
        selectors.productVideo,
      ], (product, productVideo) => {
        if(productVideo && productVideo.description) {
          return productVideo.description;
        }

        if(product && product.description) {
          return product.description;
        }

        return null;
      },
      PropTypes.string
    ],
  }),

  start: function* () {
    yield call(this.workers.fetchData);
  },

  takeLatest: ({ actions, workers }) => ({
    [actions.addToCart]: workers.addToCart,
    [actions.setLoggedIn]: workers.loginChanged,
    [actions.setLoggedOut]: workers.loginChanged,
  }),

  workers: {
    fetchData: function* () {
      const { loading, loadSuccess, loadFailed } = this.actionCreators;
      const { productId, extraProducts } = this.props;
      yield put(loading(productId));

      let result;
      try {
        result = yield call(Products.workers.fetch, {payload: {id: productId}}, true);

        let videoPlayer;
        if (result.features && result.features.videos) {
          videoPlayer = yield call(VideoPlayers.workers.fetch, {payload: {url: result.features.videos}}, true);
        } else {
          // TODO: throw error that video does not exist
        }

        if(result) {
          yield put(loadSuccess(result));
        }else {
          yield put(loadFailed())
        }

        if(this.props.rootNode) {
          try {
            if(hasDescription(result, videoPlayer)) {
              // Since we are going to show the description we load the description module before setting the loaded attribute
              const loader = () => import('~/components/video-playlist/description');
              yield call(loader);
            }

            this.props.rootNode.setAttribute('data-loaded', 'loaded');
          } catch(e) {
            console.log('error')
          }
        }
      } catch (e) {
        console.log('err', e);
        yield put(loadFailed())
      }
    },

    addToCart: function* (action) {
      const { product } = action.payload || {};

      const extraProducts = [];
      const activeCart = yield entities.get('activeCart');
      const activeShop = yield entities.get('activeShop');

      if(product) {
        if(activeCart) {
          yield all(map([product, ...extraProducts], (product) => {
            const cartItem = find(activeCart.contents, {product: product.url});

            if(cartItem) {
              // item exists
              const updatedCartItem = {
                quantity: cartItem.quantity + 1
              };
              return put(CartItems.actions.update.request(cartItem.id, updatedCartItem));
            }else {
              const newCartItem = {
                product: product.url,
                quantity: 1,
                cart: activeCart.url
              };
              return put(CartItems.actions.create.request(newCartItem));
            }
          }));
        } else {
          const config  = yield select(configSelector);

          let newCartDefaults = {};
          try {
            if(config && config.cartDefaults && config.cartDefaults) {
              newCartDefaults = omit(config.cartDefaults, ['contact', 'shipping_contact']);
            }
          } catch (e) {
            console.error(e);
          }

          const newCart = {
            ...newCartDefaults,
            shop: activeShop.url,
            contents: map([product, ...extraProducts], (product) => {
              return {product: product.url, quantity: 1};
            }),
          };

          yield put(Carts.actions.create.request(newCart))
        }
      }

    },

    loginChanged: function* (action) {
      // try {
        const videoPlayer = yield this.get('videoPlayer');

        if(videoPlayer) {
          yield call(VideoPlayers.workers.fetch, VideoPlayers.actions.fetch.request(videoPlayer.id));
        }
      // } catch(e) {
      //   console.log('error',e)
      // }

    }
  }
})
